import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper";
import useCourse from "./hooks/useCourse"; // Fetches live data from MongoDB
import Card from "../../components/Card";
import "./InternshipDetailPageDummy.css";

// Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/autoplay";

import InternshipContactForm from "../homepage/contact/InternshipContactForm";
import VideoCard from "../../components/VideoCard";
import ParticipantsTestimonials from "./testimonials-internship/ParticipantTestimonials";
import FAQs1 from "./faqs-internship/FAQs";
import BrandsCarousel from "../../components/BrandsCarousel";

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

const InternshipDetailPage = () => {
  const { courseType } = useParams();
  const navigate = useNavigate();

  // Fetch course data live from MongoDB (falls back to static data if API unavailable)
  const { courseData, loading, error } = useCourse(courseType);

  const swiperRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [_isVideoPlaying, setIsVideoPlaying] = useState(false);
  // Videos fetched from the global VideoTestimonials collection.
  // Falls back to courseData.aluminiVideos if the API returns an empty list.
  const [apiVideos, setApiVideos] = useState(null);

  const [activeTab, setActiveTab] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  // Redirect to home if course slug is unknown
  useEffect(() => {
    if (!loading && (error || !courseData)) {
      navigate("/");
    }
  }, [loading, error, courseData, navigate]);

  // Scroll to top when the course slug changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [courseType]);

  // Fetch global video testimonials for this course page
  useEffect(() => {
    if (!courseType) return;
    const fetchVideos = async () => {
      try {
        const res = await fetch(`${API_URL}/api/videos?page=${encodeURIComponent(courseType)}`);
        const json = await res.json();
        if (res.ok && json.success && Array.isArray(json.data)) {
          // Only override if the API actually returned videos for this page
          setApiVideos(json.data.length > 0 ? json.data : null);
        }
      } catch {
        setApiVideos(null); // Fall back to courseData.aluminiVideos
      }
    };
    fetchVideos();
  }, [courseType]);

  // Handle video play/pause to pause/resume Swiper autoplay
  const handleVideoPlayStateChange = (isPlaying, videoId) => {
    if (isPlaying) {
      // Pause all other videos
      const videos = document.querySelectorAll('.alumni-swiper video');
      videos.forEach(video => {
        if (video.dataset.videoId !== videoId) {
          video.pause();
        }
      });

      // Stop Swiper autoplay
      setIsVideoPlaying(true);
      if (swiperRef.current && swiperRef.current.autoplay) {
        swiperRef.current.autoplay.stop();
      }
    } else {
      // Check if any other video is still playing
      const videos = document.querySelectorAll('.alumni-swiper video');
      let anyPlaying = false;
      videos.forEach(video => {
        if (!video.paused) {
          anyPlaying = true;
        }
      });

      if (!anyPlaying) {
        setIsVideoPlaying(false);
        if (swiperRef.current && swiperRef.current.autoplay) {
          swiperRef.current.autoplay.start();
        }
      }
    }
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80; // Adjust for fixed navbar
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  if (loading) {
    return (
      <div className="internship-loading">
        <div className="loading-spinner"></div>
        <p>Loading Internship Details...</p>
      </div>
    );
  }

  if (!courseData) {
    return null;
  }

  return (
    <div className="internship-detail-page">
      <section className="hero-section" id="header">
        <div className="container">
          <h1 className="hero-title">{courseData.hero.headline}</h1>
          <center><p className="hero-subtitle">{courseData.hero.subheadline}</p></center>
          <p className="hero-description">{courseData.hero.description}</p>

          <div className="statistics-grid">
            {courseData.hero.statistics.map((stat, index) => (
              <Card key={index} className="stat-card">
                <h3>{stat.value}</h3>
                <p>{stat.label}</p>
                <p>({stat.ref})</p>
              </Card>
            ))}
          </div>

          <div className="hero-cta">
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("contact");
              }}
              className="btn primary"
            >
              Enroll Now
            </a>
            <a
              href="#program-details"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("program-details");
              }}
              className="btn light"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Infinite Brands Scroller Carousel */}
      <BrandsCarousel />

      <section className="program-highlights-section" id="highlights">
        <div className="container">
          <h2>Program Highlights</h2>
          <p className="section-subtitle">
            Duration: {courseData.programHighlights.duration}
          </p>

          {/* Why This Matters Info Box */}
          <div className="why-matters-box">
            <h3 className="why-matters-title">
              Why This Matters
            </h3>
            <p className="why-matters-intro">
              This is not a traditional internship - it is a structured training
              + internship-style project program with:
            </p>
            <ul className="why-matters-list">
              <li>Real-world portfolio development</li>
              <li>Weekly personalised mentorship</li>
              <li>A Microsoft-partnered certification</li>
              <li>A flexible schedule suitable for full-time professionals</li>
            </ul>
          </div>

          <div className="highlights-grid">
            {courseData.programHighlights.features.map((feature, index) => (
              <Card key={index} className="highlight-card">
                <span className="highlight-icon">{feature.icon}</span>
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="tools-section" id="tools">
        <div className="container">
          <h2>Tools & Technologies Covered</h2>
          <p className="section-subtitle">
            Master What Employers Actually Want
          </p>

          {isMobile ? (
            <Swiper
              slidesPerView={1.2}
              spaceBetween={15}
              pagination={{ clickable: true }}
              modules={[Pagination]}
              className=" tools-swiper"
            >
              {courseData.toolsCovered.map((tool, index) => (
                <SwiperSlide key={index}>
                  <Card className="tool-card margin-top light" style={{ height: "100%" }}>
                    {tool.image ? (
                      <img src={tool.image} alt={tool.name} />
                    ) : (
                      <div className="tool-card-fallback" aria-hidden="true">
                        {tool.name
                          .split(' ')
                          .map((part) => part[0])
                          .join('')
                          .slice(0, 3)
                          .toUpperCase()}
                      </div>
                    )}
                    <h4>{tool.name}</h4>
                    <p>{tool.description}</p>
                  </Card>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="tools-grid">
              {courseData.toolsCovered.map((tool, index) => (
                <Card key={index} className="tool-card light">
                  {tool.image ? (
                    <img src={tool.image} alt={tool.name} />
                  ) : (
                    <div className="tool-card-fallback" aria-hidden="true">
                      {tool.name
                        .split(' ')
                        .map((part) => part[0])
                        .join('')
                        .slice(0, 3)
                        .toUpperCase()}
                    </div>
                  )}
                  <h4>{tool.name}</h4>
                  <p>{tool.description}</p>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="why-program-section" id="why">
        <div className="container">
          <h2>Why Choose Us?</h2>

          {isMobile ? (
            <Swiper
              slidesPerView={1.2}
              spaceBetween={15}
              pagination={{ clickable: true }}
              modules={[Pagination]}
              className="tools-swiper"
            >
              {courseData.whyThisProgram.map((reason, index) => (
                <SwiperSlide key={index}>
                  <Card className="tool-card margin-top">
                    <h4>{reason.title}</h4>
                    <p>{reason.description}</p>
                  </Card>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="why-grid">
              {courseData.whyThisProgram.map((reason, index) => (
                <Card key={index} className="why-card">
                  <h4>{reason.title}</h4>
                  <p>{reason.description}</p>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="program-details-section" id="program-details">
        <div className="container">
          <h2>Detailed Program Information</h2>

          <Card className="detail-card">
            <h3>{courseData.programDetails.overview.title}</h3>
            <p>{courseData.programDetails.overview.description}</p>
            <div className="phases">
              {courseData.programDetails.overview.phases.map((phase, index) => (
                <div key={index} className="phase">
                  <h4>{phase.title}:</h4>
                  <p>{phase.description}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card className="detail-card">
            <h3>{courseData.programDetails.modeOfDelivery.title}</h3>
            <p>{courseData.programDetails.modeOfDelivery.description}</p>
          </Card>

          <Card className="detail-card">
            <h3>{courseData.programDetails.commitment.title}</h3>
            <p>{courseData.programDetails.commitment.description}</p>
          </Card>

          <Card className="detail-card">
            <h3>{courseData.programDetails.curriculum.title}</h3>
            <p>{courseData.programDetails.curriculum.description}</p>
            <ul>
              {courseData.programDetails.curriculum.points.map(
                (point, index) => (
                  <li key={index}>{point}</li>
                ),
              )}
            </ul>
          </Card>

          <Card className="detail-card">
            <h3>{courseData.programDetails.mentorship.title}</h3>
            <p>{courseData.programDetails.mentorship.description}</p>
          </Card>

          <Card className="detail-card">
            <h3>{courseData.programDetails.certification.title}</h3>
            <p>{courseData.programDetails.certification.description}</p>
          </Card>

          <Card className="detail-card">
            <h3>{courseData.programDetails.careerAdvancement.title}</h3>
            <p>{courseData.programDetails.careerAdvancement.description}</p>
            <p>{courseData.programDetails.careerAdvancement.additionalInfo}</p>
            <p className="career-paths">
              {courseData.programDetails.careerAdvancement.careerPaths}
            </p>
          </Card>
        </div>
      </section>

      <section className="who-should-apply-section" id="who">
        <div className="container">
          <h2>Who Should Apply?</h2>

          {isMobile ? (
            <Swiper
              slidesPerView={1.2}
              spaceBetween={15}
              pagination={{ clickable: true }}
              modules={[Pagination]}
              className="tools-swiper"
            >
              {courseData.whoShouldApply.map((person, index) => (
                <SwiperSlide key={index}>
                  <Card className="who-card margin-top">
                    <span className="who-icon">{person.icon}</span>
                    <h4>{person.title}</h4>
                    <p>{person.description}</p>
                  </Card>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="who-grid">
              {courseData.whoShouldApply.map((person, index) => (
                <Card key={index} className="who-card">
                  <span className="who-icon">{person.icon}</span>
                  <h4>{person.title}</h4>
                  <p>{person.description}</p>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="program-fee-section" id="program-fee">
        <div className="container">
          <Card className="fee-card info">
            <h2 className="fee-title">
              Program Fee & Registration
            </h2>

            <p className="fee-intro">
              This is a{" "}
              <strong>
                fee-free, 3-month Data Science Training & Internship Certificate
                Program.
              </strong>
            </p>

            <p className="fee-explanation">
              To confirm enrolment and allocate program resources, a one-time
              nominal registration fee applies.
            </p>

            <div className="fee-structure">
              <div className="fee-item standard">
                <h4>Standard Registration Fee</h4>
                <p className="fee-amount">£200</p>
              </div>

              <div className="fee-item fast-track">
                <h4>Fast-Track Selection Candidates</h4>
                <p className="fee-discount">
                  15% discount → <span className="discounted-price">£170</span>
                </p>
                <p className="fast-track-note">
                  Applications considered under the Fast-Track Selection process
                  are assessed against defined criteria, including academic
                  background, professional experience, and interpersonal
                  competencies.
                </p>
              </div>
            </div>

            <div className="payment-options">
              <p>
                💳 <strong>Payable in two feasible instalments</strong>
              </p>
            </div>

            <div className="fee-clarification">
              <p className="clarification-text">
                This registration fee covers{" "}
                <strong>administrative costs only</strong>.
              </p>
              <ul className="no-hidden-fees">
                <li>✅ No hidden charges</li>
                <li>✅ No additional fees</li>
                <li>✅ No recurring payments at any stage of the program</li>
              </ul>
              <p className="access-guarantee">
                Once registered, you receive{" "}
                <strong>
                  full access to the program for the entire duration.
                </strong>
              </p>
            </div>
          </Card>
        </div>
      </section>

      <section className="how-to-apply-section" id="how-to-apply">
        <div className="container">
          <h2 className="section-title">
            How To Apply
          </h2>

          <p className="apply-intro">
            You may apply to the program through either of the following channels:
          </p>

          {isMobile ? (
            <Swiper
              slidesPerView={1.2}
              spaceBetween={15}
              pagination={{ clickable: true }}
              modules={[Pagination]}
              className="tools-swiper"
            >
              <SwiperSlide>
                <div className="channel-box margin-top">
                  <div className="channel-icon">🌐</div>
                  <h4>Via Website</h4>
                  <p>Directly via our website by completing the application form and uploading your updated CV</p>
                </div>
              </SwiperSlide>
              <SwiperSlide>
                <div className="channel-box margin-top">
                  <div className="channel-icon">💼</div>
                  <h4>Via LinkedIn</h4>
                  <p>Via our official LinkedIn advertisement, using your updated CV</p>
                </div>
              </SwiperSlide>
            </Swiper>
          ) : (
            <div className="application-channels">
              <div className="channel-box">
                <div className="channel-icon">🌐</div>
                <h4>Via Website</h4>
                <p>Directly via our website by completing the application form and uploading your updated CV</p>
              </div>

              <div className="channel-box">
                <div className="channel-icon">💼</div>
                <h4>Via LinkedIn</h4>
                <p>Via our official LinkedIn advertisement, using your updated CV</p>
              </div>
            </div>
          )}

          <div className="application-process-info">
            <p>
              All applications are reviewed by our team in line with program eligibility and selection criteria.
            </p>
            <p>
              Shortlisted candidates will be contacted directly with the next steps, including confirmation, and onboarding details.
            </p>
          </div>
        </div>
      </section>

      <section className="how-this-works-section" id="how-this-works">
        <div className="container">
          <h2 className="section-title">
            How This Works
          </h2>

          <div className="process-timeline">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Application Submitted</h3>
                <p>
                  Applications are received via our website or official LinkedIn advertisement.
                </p>
              </div>
            </div>

            <div className="process-connector"></div>

            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Screening & Review</h3>
                <p>
                  Our team conducts an initial screening based on eligibility, background, and program fit.
                </p>
              </div>
            </div>

            <div className="process-connector"></div>

            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Consent to Proceed</h3>
                <p>
                  Shortlisted candidates are contacted and asked to confirm their intent to move forward with the program.
                </p>
              </div>
            </div>

            <div className="process-connector"></div>

            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Invoice Issued</h3>
                <p>
                  Upon confirmation, an invoice for the nominal registration fee is issued.
                </p>
              </div>
            </div>

            <div className="process-connector"></div>

            <div className="process-step">
              <div className="step-number">5</div>
              <div className="step-content">
                <h3>Registration Fee Payment</h3>
                <ul className="payment-details">
                  <li>The registration fee is payable in two feasible instalments.</li>
                  <li>
                    Candidates facing genuine financial hardship may discuss this with our team,
                    who will review the situation and advise on potential options where appropriate.
                  </li>
                </ul>
              </div>
            </div>

            <div className="process-connector"></div>

            <div className="process-step">
              <div className="step-number">6</div>
              <div className="step-content">
                <h3>Onboarding & Enrollment</h3>
                <p>
                  Once payment is received, onboarding details are shared and the candidate is formally enrolled into the program.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


      <section className="alumni-section" id="alumni">
        <div className="container">
          <h2>Voices of Our Program Alumni</h2>

          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            slidesPerView={1}
            spaceBetween={30}
            breakpoints={{
              601: { slidesPerView: 2 },
              1025: { slidesPerView: 3 }
            }}
            pagination={{ clickable: true }}
            autoplay={{ delay: 4000, disableOnInteraction: true }}
            modules={[Pagination, Autoplay]}
            className="alumni-swiper "
          >
            {/* Use global API videos if available; otherwise fall back to embedded course videos */}
            {(apiVideos ?? courseData.aluminiVideos ?? []).map((video, idx) => (
              <SwiperSlide key={video._id || video.id || idx}>
                <article className="testimonial video-margin">
                  <VideoCard
                    videoUrl={video.resolvedUrl || video.videoUrl}
                    videoId={String(video._id || video.id || idx)}
                    onPlayStateChange={handleVideoPlayStateChange}
                  />
                  <div className="testimonial__client-info">
                    <h5>{video.name}</h5>
                    <small>Completed the Program</small>
                  </div>
                </article>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      <section className="cta-section" id="apply">
        <div className="container">
          <Card className="cta-card primary">
            <h2>📨 Ready to Start?</h2>
            <p>
              Don't miss this opportunity to bridge the UK skills gap and launch
              your career! Apply now and take the first step toward a brighter
              future with Int-Elligence Tech Solutions Plc.
            </p>

            <div className="cta-buttons">
              <a href="#contact" className="btn light large">
                Enroll Now
              </a>
            </div>

            <div className="contact-info">
              <p>For any queries, contact our HR team:</p>
              <p className="email">📧 {courseData.contactEmail}</p>
            </div>

            <p className="footer-note">{courseData.footerNote}</p>
          </Card>
        </div>
      </section>

      <ParticipantsTestimonials />

      <FAQs1 />

      <section className="additional-info-section">
        <div className="container">
          <Card className="info-card">
            <h3>Additional Information</h3>
            <p>
              This is a learning-focused,{" "}
              <strong>
                Unpaid Training and Internship - Certificate Program
              </strong>{" "}
              designed to provide hands-on training and experience.
            </p>
          </Card>
        </div>
      </section>

      <InternshipContactForm />
    </div>
  );
};

export default InternshipDetailPage;
